import { CheckCircle2, X } from 'lucide-react'
import { toast } from 'sonner'

import { KeyboardEvent, useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Kbd, PosButton, Spinner } from '@/components/ui'
import { PAYMENT_METHODS } from '@/constants/payment'
import { useCart } from '@/contexts/CartContext'
import { useCreateOrder } from '@/hooks/features/orders'
import { cn } from '@/lib/utils'
import { quickTenders } from '@/services'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

import { NumPad } from './NumPad'

interface PaymentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (order: PosModel.Order) => void
}

const round = (n: number) => Math.round(n * 100) / 100

/**
 * Tender screen. The amount defaults to what is still due, so an exact card or mobile payment is one keystroke
 * (Enter). Cash shows likely notes and the change; a part payment adds a row and leaves the rest due (split tender).
 */
export const PaymentDialog = ({ open, onOpenChange, onComplete }: PaymentDialogProps) => {
  const cart = useCart()
  const createOrder = useCreateOrder()
  const [payments, setPayments] = useState<PosModel.Payment[]>([])
  const [method, setMethod] = useState<PosModel.PaymentMethod>('cash')
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')

  const total = cart.totals.total
  const paid = round(payments.reduce((s, p) => s + p.amount, 0))
  const due = round(Math.max(total - paid, 0))
  const tendered = parseFloat(amount) || 0
  const change = round(Math.max(paid + tendered - total, 0))
  const covers = paid + tendered >= total - 0.001

  useEffect(() => {
    if (!open) return
    setPayments([])
    setMethod('cash')
    setAmount(String(total))
    setReference('')
  }, [open, total])

  const submit = (all: PosModel.Payment[]) =>
    createOrder.mutate(
      {
        customer_id: cart.customer?._id,
        lines: cart.lines.map((l) => ({ product_id: l.product._id, quantity: l.quantity, discount: l.discount, note: l.note })),
        discount: cart.discount,
        adjustment: cart.totals.adjustment,
        payments: all,
      },
      {
        onSuccess: (order) => {
          cart.clear()
          onOpenChange(false)
          onComplete(order)
        },
        onError: (err) => toast.error(err.message),
      }
    )

  const confirm = () => {
    if (tendered <= 0 || createOrder.isPending) return
    // Only cash can be over-tendered; card and mobile are charged exactly what is due.
    const charge = method === 'cash' ? tendered : Math.min(tendered, due)
    const next = [...payments, { method, amount: charge, reference: reference.trim() || undefined }]
    if (covers) return submit(next)
    setPayments(next)
    setAmount(String(round(due - charge)))
    setReference('')
  }

  const onKeyDown = (e: KeyboardEvent) => {
    const m = PAYMENT_METHODS.find((p) => p.key === e.key && e.altKey)
    if (m) {
      e.preventDefault()
      setMethod(m.id)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      confirm()
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !createOrder.isPending && onOpenChange(o)}>
      <DialogContent size="lg" className="gap-0 p-0" onKeyDown={onKeyDown}>
        <div className="grid grid-cols-[minmax(0,1fr)] md:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-5 p-6">
            <DialogHeader className="static m-0 p-0">
              <DialogTitle>Take payment</DialogTitle>
              <DialogDescription>
                {cart.totals.itemCount} items{cart.customer ? ` · ${cart.customer.name}` : ''}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md bg-frame p-5 text-white">
              <p className="text-xs font-medium uppercase tracking-wide text-white/60">{payments.length ? 'Remaining' : 'Total due'}</p>
              {/* eslint-disable-next-line no-restricted-syntax -- the amount due is read across a counter; larger than any token */}
              <p className="text-[2.5rem] font-semibold leading-tight tabular-nums">{formatMoney(due)}</p>
              {payments.length > 0 && <p className="text-sm text-white/60">of {formatMoney(total)}</p>}
            </div>

            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5" role="radiogroup" aria-label="Payment method">
              {PAYMENT_METHODS.map(({ id, label, icon: Icon, key }) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={method === id}
                  onClick={() => {
                    setMethod(id)
                    if (id !== 'cash') setAmount(String(due))
                  }}
                  className={cn(
                    'relative flex h-20 flex-col items-center justify-center gap-1.5 rounded-md border text-sm font-medium transition-colors',
                    method === id ? 'border-primary bg-primary text-white' : 'border-input bg-card text-ink hover:bg-subdued'
                  )}
                >
                  <Icon className="size-5" />
                  {label}
                  <Kbd dark={method === id} className="absolute right-1.5 top-1.5">{`⌥${key}`}</Kbd>
                </button>
              ))}
            </div>

            {method === 'cash' && (
              <div className="grid grid-cols-4 gap-2">
                {quickTenders(due).map((n) => (
                  <PosButton key={n} variant="secondary" size="touch" className="rounded-md tabular-nums" onClick={() => setAmount(String(n))}>
                    {formatMoney(n).replace('.00', '')}
                  </PosButton>
                ))}
              </div>
            )}

            {method !== 'cash' && (
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={method === 'card' ? 'Card last 4 digits (optional)' : 'Transaction ID (optional)'}
                className="h-11"
              />
            )}

            {payments.length > 0 && (
              <ul className="divide-y divide-border-subtle rounded-md border border-border">
                {payments.map((p, i) => (
                  <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="capitalize text-ink">
                      {PAYMENT_METHODS.find((m) => m.id === p.method)?.label}
                      {p.reference && <span className="text-muted-foreground"> · {p.reference}</span>}
                    </span>
                    <span className="flex items-center gap-2 tabular-nums">
                      {formatMoney(p.amount)}
                      <button type="button" aria-label="Remove payment" onClick={() => setPayments(payments.filter((_, j) => j !== i))} className="grid size-6 place-items-center rounded-full text-muted-foreground hover:bg-fill">
                        <X className="size-3.5" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-col gap-3 border-t border-border bg-subdued p-6 md:border-l md:border-t-0">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="tendered">
              {method === 'cash' ? 'Cash received' : 'Amount'}
            </label>
            <Input
              id="tendered"
              autoFocus
              inputMode="decimal"
              value={amount}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              className="h-14 text-right text-2xl font-semibold tabular-nums"
            />
            <div className="flex h-10 items-center justify-between rounded-md bg-card px-3 text-sm">
              <span className="text-muted-foreground">{covers ? 'Change' : 'Still due after this'}</span>
              <span className={cn('text-heading-md tabular-nums', covers && change > 0 ? 'text-success' : 'text-ink')}>
                {formatMoney(covers ? change : round(due - tendered))}
              </span>
            </div>
            <NumPad value={amount} onChange={setAmount} />
            <PosButton size="xl" variant={covers ? 'success' : 'default'} className="mt-auto w-full" onClick={confirm} disabled={tendered <= 0 || createOrder.isPending}>
              {createOrder.isPending ? <Spinner className="size-5" /> : covers ? <CheckCircle2 /> : null}
              {covers ? 'Complete sale' : 'Add part payment'}
              <Kbd dark>↵</Kbd>
            </PosButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
